import {useState} from 'react';

import {
  AmplifyService,
  ChangePasswordInput,
  ClientMetadata,
  ConfirmSignUpInput,
  ForgotPasswordConfirmInput,
  ForgotPasswordInput,
  GetTokenInput,
  SignInInput,
  SignUpInput,
} from '@/services';
import {AuthFlowType, MFAPreferenceType} from '@/services/amplify/AmplifyTypes';
import {logBreadcrumb} from '@/utils/errors/logError';

export const useAmplifyAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    handleSignIn,
    handleSignInConfirmation,
    handleSignUp,
    handleSignUpConfirmation,
    autoSignIn,
    handleResendCodeSignUpConfirmation,
    handleChangePassword,
    handleSendRecoveryEmail,
    handleConfirmForgotPassword,
    updateMFA,
    handleSignOut,
    getCurrentSession,
    getCurrentUser,
    hasSession,
    getToken,
    clearData,
  } = AmplifyService();

  const executeAuthMethod = async (
    methodName: string,
    logData: unknown,
    method: Function,
    ...args: unknown[]
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      return await method(...args);
    } catch (err) {
      setError(err.message || 'An error occurred');
      logBreadcrumb({
        category: `AmplifyAuth.useAmplifyAuth.executeAuthMethod.${methodName}`,
        type: 'query',
        message: `Error in ${methodName}`,
        level: 'warning',
        data: {
          log: logData,
          error: err,
        },
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    signIn: (
      input: SignInInput,
      authFlowType: AuthFlowType,
      clientMetadata?: ClientMetadata,
    ) =>
      executeAuthMethod(
        'handleSignIn',
        {
          username: input.username,
          mfa: authFlowType,
          clientMetadata: clientMetadata,
        },
        handleSignIn,
        input,
        authFlowType,
        clientMetadata,
      ),
    signUp: (input: SignUpInput) =>
      executeAuthMethod(
        'handleSignUp',
        {
          username: input.username,
          email: input.email,
          phoneNumber: input.phoneNumber,
          sharedKey: input.shared_key,
        },
        handleSignUp,
        input,
      ),
    handleSignInConfirmation: (SMSCode: string, username: string) =>
      executeAuthMethod(
        'handleSignInConfirmation',
        {
          username: username,
        },
        handleSignInConfirmation,
        SMSCode,
        username,
      ),
    confirmSignUp: (input: ConfirmSignUpInput) =>
      executeAuthMethod(
        'handleSignUpConfirmation',
        {
          username: input.username,
          role: input.role,
          invitationCode: input.invitation_code,
          referralCode: input.referral_code,
          sharedKey: input.shared_key,
        },
        handleSignUpConfirmation,
        input,
      ),
    autoSignIn: () => executeAuthMethod('autoSignIn', null, autoSignIn),
    resendCodeSignUpConfirmation: (username: string) =>
      executeAuthMethod(
        'handleResendCodeSignUpConfirmation',
        {
          username: username,
        },
        handleResendCodeSignUpConfirmation,
        username,
      ),
    changePassword: (input: ChangePasswordInput) =>
      executeAuthMethod(
        'handleChangePassword',
        null,
        handleChangePassword,
        input,
      ),
    sendRecoveryEmail: (
      input: ForgotPasswordInput,
      clientMetadata?: ClientMetadata,
    ) =>
      executeAuthMethod(
        'handleSendRecoveryEmail',
        {
          username: input.username,
          clientMetadata: clientMetadata,
        },
        handleSendRecoveryEmail,
        input,
        clientMetadata,
      ),
    confirmForgotPassword: (input: ForgotPasswordConfirmInput) =>
      executeAuthMethod(
        'handleConfirmForgotPassword',
        {
          username: input.username,
        },
        handleConfirmForgotPassword,
        input,
      ),
    updateMFA: (sms: MFAPreferenceType) =>
      executeAuthMethod(
        'updateMFA',
        {
          mfa: sms,
        },
        updateMFA,
        sms,
      ),
    signOut: () => executeAuthMethod('handleSignOut', null, handleSignOut),
    getCurrentSession: (forceRefresh = false) =>
      executeAuthMethod(
        'getCurrentSession',
        null,
        getCurrentSession,
        forceRefresh,
      ),
    getCurrentUser: () =>
      executeAuthMethod('getCurrentUser', null, getCurrentUser),
    hasSession: () => executeAuthMethod('hasSession', null, hasSession),
    getToken: (input: GetTokenInput): Promise<string> =>
      executeAuthMethod(
        'getToken',
        {
          target: input.target,
          sharedKey: input.shared_key,
        },
        getToken,
        input,
      ),
    clearData: () => executeAuthMethod('clearData', null, clearData),
    isLoading,
    error,
  };
};
